import { loadGoogleMapsMapLibrary } from 'lib/utils/googleMaps/loadGoogleMapsMapLibrary';

export type GeocodedLocation = {
  latitude: number;
  longitude: number;
  city?: string | null;
  stateCode?: string | null;
};

/**
 * City and two-letter state code from a geocoder result. Google returns the
 * state abbreviation as short_name ("NY"), the full name as long_name.
 */
export const parseLocalityFromResult = (
  result?: google.maps.GeocoderResult
): { city: string | null; stateCode: string | null } => {
  const components = result?.address_components ?? [];
  const findComponent = (type: string) =>
    components.find((component) => component.types.includes(type));

  return {
    city: findComponent('locality')?.long_name ?? findComponent('postal_town')?.long_name ?? null,
    stateCode: findComponent('administrative_area_level_1')?.short_name ?? null,
  };
};

const ZIP_CODE_REGEX = /^\d{5}(-\d{4})?$/;
const CITY_NAME_REGEX = /^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/;
const STATE_NAME_REGEX = /^[A-Za-z]+(?:\s[A-Za-z]+)*$/;
const GENERAL_LOCATION_REGEX = /^(?=.*[A-Za-z])[A-Za-z0-9\s,'-]{2,}$/;

const ACCEPTED_GEOCODER_TYPES: string[] = [
  'street_address',
  'route',
  'premise',
  'locality',
  'postal_code',
  'administrative_area_level_3',
];

const ensureGoogleMapsLoaded = async (mapApiKey?: string): Promise<void> => {
  // Singleton loader reuses an existing Maps JS script when already present.
  await loadGoogleMapsMapLibrary(mapApiKey);
};

/**
 * Performs a lightweight, local format check on a raw location query.
 * Returns true for valid US ZIP codes, city/state names, or general address strings.
 */
export const isValidLocationQuery = (query: string): boolean => {
  const trimmed = query?.trim();

  if (!trimmed) {
    return false;
  }

  return (
    ZIP_CODE_REGEX.test(trimmed) ||
    CITY_NAME_REGEX.test(trimmed) ||
    STATE_NAME_REGEX.test(trimmed) ||
    GENERAL_LOCATION_REGEX.test(trimmed)
  );
};

const hasAcceptedGeocoderType = (types?: string[]): boolean => {
  if (!types || types.length === 0) {
    return false;
  }

  return types.some((type) => ACCEPTED_GEOCODER_TYPES.includes(type));
};

/**
 * Geocodes a US ZIP, city, and/or state string to coordinates.
 * Unlike getStateFromZipcodeCoveo, this accepts non–postal-code results (city/state).
 */
export const geocodeLocation = async (
  address: string,
  mapApiKey?: string
): Promise<GeocodedLocation | null> => {
  const trimmed = address?.trim();
  if (!trimmed || !isValidLocationQuery(trimmed)) {
    return null;
  }

  await ensureGoogleMapsLoaded(mapApiKey);

  if (typeof window === 'undefined' || !window.google?.maps?.Geocoder) {
    return null;
  }

  return new Promise((resolve) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(
      { address: trimmed, componentRestrictions: { country: 'US' } },
      (results, status) => {
        if (status === 'OK' && results && results.length > 0) {
          const firstResult = results[0];

          if (firstResult.partial_match || !hasAcceptedGeocoderType(firstResult.types)) {
            resolve(null);
            return;
          }

          const location = firstResult?.geometry?.location;
          const latitude = location?.lat();
          const longitude = location?.lng();

          if (typeof latitude === 'number' && typeof longitude === 'number') {
            resolve({
              latitude,
              longitude,
              ...parseLocalityFromResult(firstResult),
            });
            return;
          }
        }
        resolve(null);
      }
    );
  });
};
